import { Box, Heading, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import { Bar } from 'react-chartjs-2';
import { useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeProvider';
import { useHistory } from '@/service/club/hooks';
import { numberToBRL } from '@/utils';
import { IHistory, Progress } from '@/service/club/types';

export const portugueseMonthLabels = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function monthLabel(label: string) {
  const [month] = label.split('/');
  return portugueseMonthLabels[parseInt(month, 10) - 1]?.substring(0, 3) ?? label;
}

function splitMonthSavings(month: Progress) {
  const split = (month.progress_sales ?? []).reduce(
    (acc, sale) => {
      if (sale.kind === 'online') {
        acc.online += Number(sale.saved) || 0;
      } else {
        acc.local += Number(sale.saved) || 0;
      }
      return acc;
    },
    { local: 0, online: 0 },
  );

  if (split.local === 0 && split.online === 0 && month.progress) {
    return { local: month.progress, online: 0 };
  }

  return split;
}

function lastMonthsChronological(history?: IHistory, count = 6): Progress[] {
  if (!history?.progress?.length) return [];
  // getEconometer() already reverses the API payload to oldest → newest.
  return history.progress.slice(-count);
}

export function Econometro() {
  const { data: econometer, isLoading } = useHistory();
  const { colors } = useTheme();

  const months = useMemo(() => lastMonthsChronological(econometer), [econometer]);
  const currentMonth = econometer?.progress?.at(-1);
  const totalSaved = currentMonth?.total ?? 0;
  const monthSaved = currentMonth?.progress ?? 0;

  const data = useMemo(
    () => ({
      labels: months.map((month) => monthLabel(month.label)),
      datasets: [
        {
          label: 'Estabelecimentos',
          data: months.map((month) => splitMonthSavings(month).local),
          backgroundColor: colors.primaryColor,
          borderRadius: 4,
          maxBarThickness: 28,
        },
        {
          label: 'Cupons online',
          data: months.map((month) => splitMonthSavings(month).online),
          backgroundColor: `${colors.primaryColor}66`,
          borderRadius: 4,
          maxBarThickness: 28,
        },
      ],
    }),
    [months, colors.primaryColor],
  );

  const options = {
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { family: 'Glory', size: 12 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#EEF1F2' },
        ticks: { display: false },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          boxWidth: 10,
          font: { family: 'Glory', size: 12 },
          color: '#495057',
        },
      },
      datalabels: {
        display: false,
      },
    },
  };

  return (
    <Box
      width="100%"
      backgroundColor="white"
      padding={{ base: '18px', md: '22px' }}
      borderRadius="24px"
      boxShadow="4px 4px 10px 0px #0000002E"
    >
      <VStack align="stretch" spacing={4}>
        <Heading color="#292927" fontSize="18px" fontFamily="Glory" fontWeight={500}>
          Econômetro
        </Heading>

        {isLoading ? (
          <Skeleton height="160px" borderRadius="16px" />
        ) : (
          <>
            <HStack align="flex-end" justify="space-between" spacing={4} flexWrap="wrap">
              <Box>
                <Text color="#747474" fontSize="12px" fontFamily="Glory">
                  Você já economizou
                </Text>
                <Heading color="#292927" fontSize={{ base: '28px', md: '32px' }} fontFamily="Glory" fontWeight={600}>
                  {numberToBRL(totalSaved)}
                </Heading>
              </Box>
              <Box textAlign="right">
                <Text color="#747474" fontSize="12px" fontFamily="Glory">
                  Neste mês
                </Text>
                <Text color={colors.primaryColor} fontSize="18px" fontFamily="Glory" fontWeight={600}>
                  {numberToBRL(monthSaved)}
                </Text>
              </Box>
            </HStack>

            <Box height="180px">
              <Bar options={options} data={data} />
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
}
